/**
 * Goal progress computation.
 *
 * Progress is DERIVED, never stored. Anything that displays a goal percentage
 * calls computeGoalProgress() so every surface agrees. (Goal.progress is a
 * legacy field that was written 0 at creation and never updated — it is
 * ignored here; Goal.progress_override is the only stored value that counts.)
 *
 * These functions are pure and synchronous so that:
 *   - sub-goals can roll up recursively (impossible with the old async version)
 *   - callers can compute inline during render, with no polling
 *
 * Habit completion counts are passed in as a plain map rather than fetched,
 * keeping this layer free of storage concerns.
 */
import { taskProgress } from '../models/task';
import { habitPausedOn, dateKey, parseDateKey } from './habit-schedule';
import { deadlineDateKey, daysBetweenKeys } from './deadline';
import { projectRollup } from './project-tree';
const DAY_MS = 86_400_000;
/** Days covered by one occurrence of a recurrence frequency. */
function periodDays(frequency) {
    switch (frequency) {
        case 'weekly': return 7;
        case 'monthly': return 30;
        case 'yearly': return 365;
        default: return 1; // daily
    }
}
/** Nominal length of a time horizon, used when a goal has no target_date. */
function horizonDays(goal) {
    switch (goal.time_horizon) {
        case 'monthly': return 30;
        case 'quarterly': return 91;
        default: return 365;
    }
}
/**
 * The goal's time window: created_at → target_date, falling back to the
 * nominal horizon length when no target date is set.
 */
export function goalWindow(goal, now = new Date()) {
    const start = goal.created_at ? new Date(goal.created_at) : now;
    // A bare target date is a LOCAL day. `new Date('2026-10-10')` is UTC
    // midnight — the afternoon of the 10th in Auckland, the evening of the 9th
    // in LA — which put "days left" one off either side of Greenwich.
    const targetKey = goal.target_date ? deadlineDateKey(goal.target_date) : null;
    const end = targetKey
        ? (parseDateKey(targetKey) ?? new Date(NaN))
        : new Date(start.getTime() + horizonDays(goal) * DAY_MS);
    return { start, end };
}
function computePacing(goal, progress, now) {
    if (!goal.target_date) {
        return { elapsed_pct: 0, days_remaining: null, status: 'no-target' };
    }
    const { start, end } = goalWindow(goal, now);
    const total = end.getTime() - start.getTime();
    const elapsed = now.getTime() - start.getTime();
    const elapsed_pct = total > 0 ? Math.max(0, Math.min(100, (elapsed / total) * 100)) : 100;
    // Whole calendar days between today and the target day: 0 on the day
    // itself, the same count as a task's deadline.
    const targetKey = deadlineDateKey(goal.target_date);
    const days_remaining = targetKey ? daysBetweenKeys(dateKey(now), targetKey) : null;
    // A 10-point band either side keeps the label from flapping day to day.
    let status = 'on-track';
    if (progress > elapsed_pct + 10)
        status = 'ahead';
    else if (progress < elapsed_pct - 10)
        status = 'behind';
    return { elapsed_pct: Math.round(elapsed_pct), days_remaining, status };
}
/** Per-habit sorted dates, built once per instances array. */
const datesByHabit = new WeakMap();
function habitDates(instances, habitId) {
    let index = datesByHabit.get(instances);
    if (!index) {
        index = new Map();
        for (const i of instances) {
            const list = index.get(i.habit_id);
            if (list)
                list.push(i.date);
            else
                index.set(i.habit_id, [i.date]);
        }
        for (const list of index.values())
            list.sort();
        datesByHabit.set(instances, index);
    }
    return index.get(habitId) ?? [];
}
function completionsIn(completions, habitId, from, to) {
    if (!Array.isArray(completions))
        return completions[habitId] ?? 0;
    let n = 0;
    for (const date of habitDates(completions, habitId)) {
        if (date > to)
            break;
        if (date >= from)
            n += 1;
    }
    return n;
}
/**
 * Compute a goal's progress by rolling up everything linked to it.
 *
 * Weighting: every direct contributor counts equally — one sub-goal, one
 * project, one task, and one habit each carry the same weight. That is not
 * strictly proportional to effort, but it is predictable and explainable,
 * which matters more when a user is looking at the number and asking "why".
 * Use progress_override when a goal genuinely can't be measured this way.
 *
 * @param habitCompletionCounts the habit completion records (counted inside
 *   this goal's window, up to `now`), or a pre-counted map — see HabitCompletions
 * @param visited guards against cycles in parent_goal_id
 */
export function computeGoalProgress(goal, allGoals, allTasks, allProjects, habitCompletionCounts = {}, now = new Date(), visited = new Set()) {
    const contributors = [];
    // Cycle guard: a goal that is its own ancestor contributes nothing.
    if (visited.has(goal.id)) {
        return {
            goal_id: goal.id,
            goal_title: goal.title,
            progress: 0,
            is_manual: false,
            contributors: [],
            counts: {
                subgoals: 0, subgoals_achieved: 0, projects: 0,
                tasks: 0, tasks_completed: 0, habits: 0, habit_completions: 0,
            },
            pacing: computePacing(goal, 0, now),
            overall_progress: 0,
            total_tasks: 0,
            completed_tasks: 0,
            linked_projects: 0,
            linked_habits: 0,
            linked_direct_tasks: 0,
            projects: [],
            habits: [],
            directTasks: [],
            subGoals: [],
            keyResults: [],
        };
    }
    const nextVisited = new Set(visited).add(goal.id);
    // ── Sub-goals (recursive) ──────────────────────────────────────────────
    const subGoals = allGoals.filter(g => g.parent_goal_id === goal.id);
    let subgoalsAchieved = 0;
    const subGoalsDetail = [];
    for (const sub of subGoals) {
        const subResult = computeGoalProgress(sub, allGoals, allTasks, allProjects, habitCompletionCounts, now, nextVisited);
        if (sub.status === 'achieved')
            subgoalsAchieved++;
        // An achieved sub-goal counts as complete regardless of its own roll-up —
        // the user explicitly said it's done.
        const effective = sub.status === 'achieved' ? 100
            : sub.status === 'abandoned' ? 0
                : subResult.progress;
        contributors.push({
            kind: 'subgoal',
            id: sub.id,
            label: sub.title,
            progress: effective,
            detail: sub.status === 'achieved' ? 'achieved'
                : sub.status === 'abandoned' ? 'abandoned'
                    : `${effective}%`,
        });
        subGoalsDetail.push({
            goal_id: sub.id,
            goal_title: sub.title,
            status: sub.status,
            progress: effective,
        });
    }
    // ── Linked projects (task completion within the project) ───────────────
    const linkedProjects = allProjects.filter(p => (goal.linked_project_ids ?? []).includes(p.id));
    let projectTasksTotal = 0;
    let projectTasksDone = 0;
    const projectsDetail = [];
    for (const p of linkedProjects) {
        // The project page's own roll-up: the whole subtree, archived work left
        // out. Counting only tasks filed directly on the project made a goal
        // linked to a parent project read 0/0 while its project page showed the
        // work happening in sub-projects.
        const rollup = projectRollup(allProjects, allTasks, p.id);
        const done = rollup.completed;
        projectTasksTotal += rollup.total;
        projectTasksDone += done;
        // Average per-task progress, not done/total — a countable task at 2/5
        // contributes its 40% instead of reading as untouched.
        const pct = rollup.progress;
        contributors.push({
            kind: 'project',
            id: p.id,
            label: p.name,
            progress: Math.round(pct),
            detail: `${done}/${rollup.total} tasks`,
        });
        projectsDetail.push({
            project_id: p.id,
            project_name: p.name,
            total_tasks: rollup.total,
            completed_tasks: done,
            progress: pct,
        });
    }
    // ── Linked one-off tasks (binary) ──────────────────────────────────────
    const linkedTaskIds = goal.linked_task_ids ?? [];
    const directTasks = allTasks.filter(t => !t.is_habit && linkedTaskIds.includes(t.id));
    let directDone = 0;
    const directTasksDetail = [];
    for (const t of directTasks) {
        const done = t.status === 'completed';
        if (done)
            directDone++;
        const pct = Math.round(taskProgress(t));
        contributors.push({
            kind: 'task',
            id: t.id,
            label: t.title,
            progress: pct,
            detail: done ? 'done'
                : (t.target_count ?? 0) > 0 ? `${Math.max(0, t.current_count ?? 0)}/${t.target_count}${t.count_unit ? ' ' + t.count_unit : ''}`
                    : 'open',
        });
        directTasksDetail.push({
            task_id: t.id,
            task_title: t.title,
            status: t.status,
            project_name: allProjects.find(p => p.id === t.project_id)?.name,
        });
    }
    // ── Linked habits (adherence across the goal window) ───────────────────
    // A habit's contribution is completions ÷ expected-occurrences over the
    // window, capped at 100. This is what makes a habit-driven goal move at
    // all — previously habits were tallied but contributed nothing.
    const linkedHabitIds = goal.linked_habit_ids ?? [];
    const habits = allTasks.filter(t => t.is_habit && linkedHabitIds.includes(t.id));
    const { start, end } = goalWindow(goal, now);
    const windowEnd = now < end ? now : end; // only judge elapsed time
    const windowDays = Math.max(1, Math.ceil((windowEnd.getTime() - start.getTime()) / DAY_MS));
    let habitCompletions = 0;
    const habitsDetail = [];
    for (const h of habits) {
        const completions = completionsIn(habitCompletionCounts, h.id, dateKey(start), dateKey(windowEnd));
        habitCompletions += completions;
        const expected = Math.max(1, Math.floor(windowDays / periodDays(h.recurrence?.frequency)));
        const pct = Math.min(100, (completions / expected) * 100);
        contributors.push({
            kind: 'habit',
            id: h.id,
            label: h.title,
            progress: Math.round(pct),
            detail: `${completions} of ${expected} expected`,
        });
        habitsDetail.push({
            habit_id: h.id,
            habit_title: h.title,
            is_active: !habitPausedOn(h, dateKey(now)) && !h.is_archived,
            frequency: h.recurrence?.frequency || 'daily',
            completion_count: completions,
            adherence: Math.round(pct),
            expected_count: expected,
        });
    }
    // ── Key results (countable targets) ────────────────────────────────────
    // These state the target directly, so they don't need a proxy: a goal that
    // says "2 projects" is measured against 2, not against the task list of one
    // linked project.
    const keyResultsDetail = [];
    for (const kr of goal.key_results ?? []) {
        const pct = kr.target > 0
            ? Math.min(100, Math.max(0, (kr.current / kr.target) * 100))
            : 0;
        contributors.push({
            kind: 'key_result',
            id: kr.id,
            label: kr.label,
            progress: Math.round(pct),
            detail: `${kr.current}/${kr.target}${kr.unit ? ' ' + kr.unit : ''}`,
        });
        keyResultsDetail.push({
            id: kr.id,
            label: kr.label,
            target: kr.target,
            current: kr.current,
            unit: kr.unit,
            progress: Math.round(pct),
        });
    }
    // ── Roll up: equal weight per contributor ──────────────────────────────
    const derived = contributors.length > 0
        ? contributors.reduce((sum, c) => sum + c.progress, 0) / contributors.length
        : 0;
    const override = goal.progress_override;
    const hasOverride = override !== null && override !== undefined;
    const progress = Math.round(hasOverride ? Math.max(0, Math.min(100, override)) : derived);
    const totalTasks = projectTasksTotal + directTasks.length;
    const completedTasks = projectTasksDone + directDone;
    return {
        goal_id: goal.id,
        goal_title: goal.title,
        progress,
        is_manual: hasOverride,
        contributors,
        counts: {
            subgoals: subGoals.length,
            subgoals_achieved: subgoalsAchieved,
            projects: linkedProjects.length,
            tasks: totalTasks,
            tasks_completed: completedTasks,
            habits: habits.length,
            habit_completions: habitCompletions,
        },
        pacing: computePacing(goal, progress, now),
        overall_progress: progress,
        total_tasks: totalTasks,
        completed_tasks: completedTasks,
        linked_projects: linkedProjects.length,
        linked_habits: habits.length,
        linked_direct_tasks: directTasks.length,
        projects: projectsDetail,
        habits: habitsDetail,
        directTasks: directTasksDetail,
        subGoals: subGoalsDetail,
        keyResults: keyResultsDetail,
    };
}
/**
 * Convenience: progress for many goals in one pass, keyed by goal id.
 * Cheaper than calling computeGoalProgress per goal from a component.
 */
export function computeAllGoalProgress(goals, allTasks, allProjects, habitCompletionCounts = {}, now = new Date()) {
    const out = {};
    for (const g of goals) {
        out[g.id] = computeGoalProgress(g, goals, allTasks, allProjects, habitCompletionCounts, now);
    }
    return out;
}
//# sourceMappingURL=goal-progress.js.map